import { Heart } from "lucide-react";
import { Outlet, useLocation } from "react-router-dom";

import { InitLoader, SelectionGuard, Sidebar, Topbar } from "@/components/shared";
import { routesData } from "@/data";
import { t, useAppInit, useProjectSprints, useProjectsListLite, useSettings } from "@/hooks";
import { useProjectStore, useSprintStore, useSidebarStore } from "@/store";
import { cn } from "@/utils";

const SPRINT_REQUIRED_ROUTES = new Set<string>([
    routesData.dashboard, routesData.gantt, routesData.time, routesData.meetings,
    routesData.deliveryAnalytics, routesData.tasks, routesData.blockers,
    routesData.decisions, routesData.communication, routesData.workload,
    routesData.analytics, routesData.ownership, routesData.handoffs,
    routesData.reports, routesData.commentsLog, routesData.redFlags,
    routesData.alerts, routesData.reminders,
]);

const PROJECT_REQUIRED_ROUTES = new Set<string>([routesData.sprints]);

export const DashboardLayout = () => {
    const isOpen = useSidebarStore((s) => s.isOpen);
    const [settings] = useSettings();
    const { isReady } = useAppInit();
    const location = useLocation();

    const activeProjectId = useProjectStore((s) => s.activeProjectId);
    const activeSprintId = useSprintStore((s) => s.activeSprintId);
    const { projects, isLoading: projectsLoading } = useProjectsListLite();
    const { isLoading: sprintsLoading } = useProjectSprints(activeProjectId);

    if (!isReady) return <InitLoader />;

    const needsSprint = SPRINT_REQUIRED_ROUTES.has(location.pathname);
    const needsProject = needsSprint || PROJECT_REQUIRED_ROUTES.has(location.pathname);

    let guard: React.ReactNode = null;
    if (needsProject && !projectsLoading) {
        if (projects.length === 0) {
            guard = <SelectionGuard type="no-projects" />;
        } else if (needsSprint && !activeProjectId) {
            guard = <SelectionGuard type="no-projects" />;
        } else if (needsSprint && activeProjectId && !activeSprintId && !sprintsLoading) {
            guard = <SelectionGuard type="no-active-sprints" />;
        }
    }

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <Sidebar />
            <div className={cn("transition-all duration-300 flex-1 flex flex-col", isOpen ? "ms-[var(--spacing-sidebar)]" : "ms-16", "max-lg:ms-0")}>
                <Topbar />
                <main className={cn("flex-1 flex flex-col transition-all", settings.compactView ? "p-2 sm:p-3" : "p-3 sm:p-6")}>
                    {guard ?? <Outlet />}
                </main>
                <footer className="border-t border-border py-4 px-6">
                    <p className="text-center text-sm text-text-muted flex items-center justify-center gap-1.5 group">
                        {t("Developed with")}
                        <Heart className="h-4 w-4 text-error animate-[heartbeat_1.5s_ease-in-out_infinite] group-hover:scale-125 transition-transform" />
                        {t("by")}
                        <a
                            href="https://hawary.dev"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-primary hover:text-primary-dark transition-colors duration-200 hover:underline underline-offset-2"
                        >
                            Mohamed Elhawary
                        </a>
                    </p>
                </footer>
            </div>
        </div>
    );
};
